import { postgresApiClient } from 'api/trpc-pg/trpc-client'
import * as Sentry from '@sentry/react'
import { snackbarError } from 'reusable/mui/snackbar/snackbar'
import {
   CALENDAR_OAUTH_PROVIDER_KEY,
   CALENDAR_OAUTH_RETURN_PATH_KEY,
   hasState,
   isValidProvider,
   requiresAdminApproval,
   stashConnectedAnnouncement,
} from './calendar-oauth-utils'
import variables from 'variables/variables'

export interface CalendarOAuthCallbackResult {
   /** Path (without region prefix) to navigate to next. */
   returnPath: string
   status: 'success' | 'error'
}

/**
 * Where to return when the connect flow stashed nothing. The app root, because the state is an
 * opaque token and the callback URL carries no org to route back to.
 */
const FALLBACK_RETURN_PATH = '/'

/**
 * Complete the calendar OAuth flow from the callback URL: read `code`/`state` and the provider
 * stashed at connect time, then call `completeOAuth`, which is the authoritative `state` check.
 * Returns where to navigate next; the component owns the region-prefixed navigation.
 */
export async function completeCalendarOAuthCallback(params: { searchParams: URLSearchParams }): Promise<CalendarOAuthCallbackResult> {
   const { searchParams } = params
   const state = searchParams.get('state')
   const returnPath = sessionStorage.getItem(CALENDAR_OAUTH_RETURN_PATH_KEY) || FALLBACK_RETURN_PATH
   sessionStorage.removeItem(CALENDAR_OAUTH_RETURN_PATH_KEY)

   const code = searchParams.get('code')
   const provider = sessionStorage.getItem(CALENDAR_OAUTH_PROVIDER_KEY)
   sessionStorage.removeItem(CALENDAR_OAUTH_PROVIDER_KEY)

   const oauthError = searchParams.get('error')
   if (oauthError) {
      const errorDescription = searchParams.get('error_description')
      Sentry.captureException(new Error(`Calendar OAuth error from provider: ${oauthError}`), {
         tags: { component: 'completeCalendarOAuthCallback' },
         extra: { errorDescription, adminApprovalRequired: requiresAdminApproval(errorDescription) },
      })
      snackbarError(requiresAdminApproval(errorDescription)
         ? `Your organisation needs to approve ${variables.gapName} before you can connect a calendar. Ask your admin to approve it, then try again.`
         : 'We had trouble connecting your calendar. Try again.')
      return { returnPath, status: 'error' }
   }

   if (!code || !hasState(state) || !isValidProvider(provider)) {
      Sentry.captureException(new Error('Calendar OAuth callback is missing or has invalid parameters'), {
         tags: { component: 'completeCalendarOAuthCallback' },
         extra: { hasCode: !!code, hasState: hasState(state), provider },
      })
      snackbarError('The calendar connection could not be completed. Try again.')
      return { returnPath, status: 'error' }
   }

   try {
      await postgresApiClient.recallCalendar.completeOAuth.mutate({ provider, authCode: code, state: state as string })
      // Stashed rather than shown here: this route is a blank redirect stop, so the Calendar tab
      // announces it on arrival instead, alongside the card flipping to connected.
      stashConnectedAnnouncement()
      return { returnPath, status: 'success' }
   } catch (error: unknown) {
      Sentry.captureException(error, { tags: { component: 'completeCalendarOAuthCallback' } })
      snackbarError('We had trouble connecting your calendar. Try again.')
      return { returnPath, status: 'error' }
   }
}
